import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Video } from '@/types/video';
import { formatViews } from '@/utils/format';

interface HeroCarouselProps {
  videos: Video[];
}

export default function HeroCarousel({ videos }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused || videos.length <= 1) return undefined;
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % videos.length);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [paused, videos.length]);

  if (videos.length === 0) return null;
  const active = videos[index];

  const go = (dir: number) => {
    setIndex((prev) => (prev + dir + videos.length) % videos.length);
  };

  return (
    <section
      className="relative w-full h-[540px] md:h-[680px] overflow-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {videos.map((video, i) => (
        <div
          key={video.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
        >
          <img
            src={video.banner || video.cover}
            alt={video.title}
            title={`${video.title} - 啪啪视频焦点推荐`}
            className={`w-full h-full object-cover object-top ${i === index ? 'pv-ken-burns' : ''}`}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background-950/95 via-background-950/75 to-background-950/20" />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/10 to-transparent" />
        </div>
      ))}

      <div className="relative h-full flex items-center pt-16">
        <div className="w-full px-4 md:px-6">
          <div key={active.id} className="max-w-2xl pv-fade-up">
            <div className="flex items-center gap-2 mb-4">
              <span className="px-2.5 py-1 rounded-md bg-accent-500 text-background-50 text-xs font-bold">焦点推荐</span>
              <span className="px-2.5 py-1 rounded-md bg-background-900/60 text-foreground-900 text-xs font-medium backdrop-blur">
                {active.categoryName}
              </span>
            </div>
            <h1 className="font-heading text-3xl md:text-5xl font-extrabold text-foreground-950 leading-tight">
              {active.title}
            </h1>
            <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-foreground-700">
              <span className="flex items-center gap-1 text-accent-400 font-semibold">
                <i className="ri-star-fill text-xs" />
                {active.rating.toFixed(1)}
              </span>
              <span>{active.year}</span>
              <span>{active.region}</span>
              <span>{active.duration}</span>
              <span>{formatViews(active.views)}次播放</span>
            </div>
            <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed line-clamp-3 max-w-xl">
              {active.description}
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link
                to={`/video/${active.id}`}
                className="inline-flex items-center gap-2 h-11 px-5 rounded-md bg-accent-500 text-background-50 text-sm font-semibold hover:bg-accent-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-play-fill text-lg" />
                立即观看
              </Link>
              <Link
                to={`/category/${active.categoryId}`}
                className="inline-flex items-center gap-2 h-11 px-5 rounded-md border border-background-700/70 text-foreground-900 text-sm font-medium hover:border-accent-500 hover:text-accent-500 transition-colors whitespace-nowrap"
              >
                <i className="ri-apps-2-line text-base" />
                浏览同类
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 left-4 right-4 md:left-6 md:right-6 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {videos.map((video, i) => (
            <button
              key={video.id}
              type="button"
              aria-label={`切换到 ${video.title}`}
              onClick={() => setIndex(i)}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === index ? 'w-8 bg-accent-500' : 'w-3 bg-background-700 hover:bg-background-600'
              }`}
            />
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => go(-1)}
            aria-label="上一个"
            className="w-10 h-10 flex items-center justify-center rounded-md border border-background-700/70 bg-background-950/40 text-foreground-900 hover:border-accent-500 hover:text-accent-500 transition-colors backdrop-blur"
          >
            <i className="ri-arrow-left-s-line text-xl" />
          </button>
          <button
            type="button"
            onClick={() => go(1)}
            aria-label="下一个"
            className="w-10 h-10 flex items-center justify-center rounded-md border border-background-700/70 bg-background-950/40 text-foreground-900 hover:border-accent-500 hover:text-accent-500 transition-colors backdrop-blur"
          >
            <i className="ri-arrow-right-s-line text-xl" />
          </button>
        </div>
      </div>
    </section>
  );
}